import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import Modal from '../../BaseComponents/Modal';
import Button from '../../BaseComponents/Button';
import DatePickerComp from '../../BaseComponents/DatePicker';
import { ReactComponent as TrashIcon } from '../../../assets/icons/TrashBin2.svg';
import './FollowOnModal.scss';
import TaggedUserDropdown from '../../Members/MemberInfoCard/MemberNotes/TaggedUserDropdown';
import { TaggedUser } from '../../../api/staff';
import { getFullImageUrl } from '../../../utils/constants';
import { putFile } from '../../../api/fileStorage';

const CustomTrixEditor = React.forwardRef<{ insertTag: (user: TaggedUser) => void }, {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  onTagDetect?: (query: string, position: { top: number; left: number }) => void;
  onTagClose?: () => void;
}>(({ value, onChange, placeholder, className, readOnly = false, onTagDetect, onTagClose }, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  onChangeRef.current = onChange;
  valueRef.current = value;
  const inputId = useMemo(() => `trix-input-${Math.random().toString(36).substr(2, 9)}`, []);

  React.useImperativeHandle(ref, () => ({
    insertTag: (user: TaggedUser) => {
      const editor = editorRef.current;
      if (!editor) return;
      const trixEditor = editor.querySelector('trix-editor') as any;
      if (!trixEditor) return;

      const attachment = new (window as any).Trix.Attachment({
        content: `<span class="tagged-user" data-user-id="${user.id}">@${user.display_name}</span>`
      });

      const range = trixEditor.editor.getSelectedRange();
      const text = trixEditor.editor.getDocument().toString();
      const textBefore = text.substring(0, range[0]);
      const lastAt = textBefore.lastIndexOf('@');

      if (lastAt !== -1) {
        trixEditor.editor.setSelectedRange([lastAt, range[0]]);
        trixEditor.editor.insertAttachment(attachment);
        trixEditor.editor.insertString(" ");
      }
    }
  }));

  useEffect(() => {
    const input = inputRef.current;
    const editor = editorRef.current;
    if (!input || !editor) return;

    const trixEditor = document.createElement('trix-editor');
    trixEditor.setAttribute('input', inputId);
    if (placeholder) {
      trixEditor.setAttribute('placeholder', placeholder);
    }
    if (readOnly) {
      trixEditor.setAttribute('contenteditable', 'false');
    }

    editor.innerHTML = '';
    editor.appendChild(trixEditor);
    if (readOnly) {
      editor.classList.add('trix-editor-wrapper--readonly');
    } else {
      editor.classList.remove('trix-editor-wrapper--readonly');
    }

    input.value = valueRef.current || '';
    // Use type assertion safely to access editor property
    const element = trixEditor as unknown as { editor: { loadHTML: (html: string) => void; insertHTML: (html: string) => void } };
    element.editor.loadHTML(valueRef.current || '');

    // Handle file attachments - upload images to server and get full S3 URL
    const handleFileAccept = async (event: any) => {
      if (readOnly) {
        event.preventDefault();
        return;
      }

      const file = event.file;
      if (!file || !file.type.startsWith('image/')) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      try {
        const uploadResponse = await putFile({ file: file, file_type: file.type });

        if (!uploadResponse || (!uploadResponse.full_key && !uploadResponse.url)) {
          alert('Failed to upload image. Please try again.');
          return;
        }

        // Determine the correct key or URL to use
        const key = uploadResponse.full_key || (uploadResponse.url ? extractKeyFromUrl(uploadResponse.url) : null);

        if (!key) {
          alert('Failed to upload image. Please try again.');
          return;
        }

        const fullImageUrl = getFullImageUrl(key);
        const imgHtml = `<img src="${fullImageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto;">`;
        element.editor.insertHTML(imgHtml);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      }
    };

    const handleChange = () => {
      if (readOnly) return;
      onChangeRef.current(trixEditor.innerHTML);
    };

    const handleSelectionChange = () => {
      if (readOnly || !onTagDetect || !onTagClose) return;

      const editor = (trixEditor as any).editor;
      const range = editor.getSelectedRange();

      // Only handle collapsed selections (caret position)
      if (range[0] !== range[1]) {
        onTagClose();
        return;
      }

      const cursorPosition = range[0];
      const text = editor.getDocument().toString();

      // Look back from cursor to find @
      const textBeforeCursor = text.substring(0, cursorPosition);
      const lastAtSymbolIndex = textBeforeCursor.lastIndexOf('@');

      if (lastAtSymbolIndex !== -1) {
        const query = textBeforeCursor.substring(lastAtSymbolIndex + 1);

        // ensure no spaces in query (simple tag detection)
        if (!query.includes(' ') && !query.includes('\n')) {
          const selectionRect = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
          const container = editorRef.current?.closest('.textarea-container');

          if (selectionRect && container) {
            const containerRect = container.getBoundingClientRect();
            onTagDetect(query, {
              top: selectionRect.bottom - containerRect.top,
              left: selectionRect.left - containerRect.left
            });
            return;
          }
        }
      }

      onTagClose();
    };

    trixEditor.addEventListener('trix-file-accept', handleFileAccept);
    trixEditor.addEventListener('trix-change', handleChange);
    trixEditor.addEventListener('trix-selection-change', handleSelectionChange);
    trixEditor.addEventListener('keyup', handleSelectionChange);

    return () => {
      trixEditor.removeEventListener('trix-file-accept', handleFileAccept);
      trixEditor.removeEventListener('trix-change', handleChange);
      trixEditor.removeEventListener('trix-selection-change', handleSelectionChange);
      trixEditor.removeEventListener('keyup', handleSelectionChange);
    };
  }, [inputId, placeholder, readOnly, onTagDetect, onTagClose]);

  // Handle external value changes (e.g. template selection)
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const trixEditor = editor.querySelector('trix-editor');
    if (!trixEditor) return;

    // Only update if the value is different and we're not currently editing (to avoid cursor jumps)
    // When selecting a template, the editor won't be the active element.
    if (value !== trixEditor.innerHTML && document.activeElement !== trixEditor) {
      const element = trixEditor as unknown as { editor: { loadHTML: (html: string) => void } };
      element.editor.loadHTML(value || '');
    }
  }, [value]);

  return (
    <div className={className}>
      <input ref={inputRef} id={inputId} type="hidden" />
      <div ref={editorRef} />
    </div>
  );
});

const extractKeyFromUrl = (urlOrKey: string): string => {
  if (!urlOrKey) return '';

  if (!urlOrKey.startsWith('http://') && !urlOrKey.startsWith('https://')) {
    return urlOrKey;
  }

  try {
    const url = new URL(urlOrKey);
    let path = url.pathname;
    if (path.startsWith('/')) {
      path = path.substring(1);
    }

    return path || urlOrKey;
  } catch (error) {
    const s3Pattern = /s3\.([^.]+)\.amazonaws\.com\/(.+)/;
    const match = urlOrKey.match(s3Pattern);
    if (match && match[2]) {
      return match[2];
    }

    return urlOrKey;
  }
};

interface FollowOnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { followOnDate: string; description: string; mentioned_user_ids: string[] }) => void;
  onDelete?: () => void;
  isEditMode?: boolean;
  initialDate?: Date | null;
  initialDescription?: string;
}

const FollowOnModal: React.FC<FollowOnModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  isEditMode = false, 
  initialDate = null, 
  initialDescription = '' 
}) => {
  const [followOnDate, setFollowOnDate] = useState<Date | null>(null);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ followOnDate?: string; description?: string }>({});
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagDropdownPosition, setTagDropdownPosition] = useState<{
    top: number;
    left: number;
    placement?: 'above' | 'below';
    width?: number;
  }>({ top: 0, left: 0 });
  const trixEditorRef = useRef<{ insertTag: (user: TaggedUser) => void }>(null);
  const [tagSearchQuery, setTagSearchQuery] = useState('');

  const extractMentionedUserIds = (description?: string): string[] => {
    if (!description) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(description, 'text/html');
    const mentionedUsers = new Set<string>();

    const collectMentionedIds = (root: ParentNode) => {
      const taggedElements = root.querySelectorAll('.tagged-user[data-user-id]');
      taggedElements.forEach((el) => {
        const userId = el.getAttribute('data-user-id');
        if (userId) {
          mentionedUsers.add(userId);
        }
      });
    };

    collectMentionedIds(doc);

    const trixAttachmentElements = doc.querySelectorAll('[data-trix-attachment]');
    trixAttachmentElements.forEach((el) => {
      const trixAttachment = el.getAttribute('data-trix-attachment');
      if (!trixAttachment) return;

      try {
        const attachmentData = JSON.parse(trixAttachment) as { content?: string };
        if (!attachmentData.content) return;

        const contentDoc = parser.parseFromString(attachmentData.content, 'text/html');
        collectMentionedIds(contentDoc);
      } catch (error) {
      }
    });

    return Array.from(mentionedUsers);
  };

  React.useEffect(() => {
    if (isOpen) {
      setFollowOnDate(initialDate);
      setDescription(initialDescription);
      setErrors({});
    } else {
      setFollowOnDate(null);
      setDescription('');
      setErrors({});
    }
  }, [isOpen, initialDate, initialDescription]);

  const handleSave = () => {
    const newErrors: { followOnDate?: string; description?: string } = {};
    if (!followOnDate) newErrors.followOnDate = 'Follow-on Date is required';
    if (!description.trim()) newErrors.description = 'Description is required';
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    onSave({
      followOnDate: followOnDate!.toISOString(),
      description,
      mentioned_user_ids: extractMentionedUserIds(description),
    });
  };

  const handleClose = () => {
    setFollowOnDate(null);
    setDescription('');
    setErrors({});
    onClose();
  };

  const handleTagClose = useCallback(() => {
    setShowTagDropdown(false);
    setTagSearchQuery('');
  }, []);

  const handleInsertTag = useCallback((user: TaggedUser) => {
    if (trixEditorRef.current) {
      trixEditorRef.current.insertTag(user);
    }
    handleTagClose();
  }, [handleTagClose]);

  const handleTagDetect = useCallback((query: string, position: { top: number; left: number }) => {
    setTagSearchQuery(query);
    setTagDropdownPosition(position);
    setShowTagDropdown(true);
  }, []);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title={isEditMode ? "Edit Follow-on" : "Add Follow-on"} 
      width="520px"
    >
      <div className="follow-on-modal">
        <div className="form-group">
          <label className="label-title">Follow-on Date *</label>
          <DatePickerComp
            onDateChange={(d) => {
              setFollowOnDate(d);
              if (errors.followOnDate) setErrors(prev => ({ ...prev, followOnDate: undefined }));
            }}
            selectedDate={followOnDate}
            placeholder='dd/mm/yyyy --:--'
            showTimeInput
            showMonthDropdown
            showYearDropdown
            showUKTime
          />
          {errors.followOnDate && <span className="error-message">{errors.followOnDate}</span>}
        </div>

        <div className="form-group">
          <label className="label-title">Description *</label>
          <div className='textarea-container'>
            <div className={`trix-editor-wrapper  trix-editor-wrapper--readonly`}>
              <CustomTrixEditor
                ref={trixEditorRef}
                value={description}
                onChange={(html: string) => setDescription(html)}
                placeholder="Enter Note"
                className="trix-editor"
                onTagDetect={handleTagDetect}
                onTagClose={handleTagClose}
              />
              <TaggedUserDropdown
                isVisible={showTagDropdown}
                position={tagDropdownPosition}
                onSelectUser={handleInsertTag}
                onClose={handleTagClose}
                searchQuery={tagSearchQuery}
              />
            </div>
          </div>
          {errors.description && <span className="error-message">{errors.description}</span>}
        </div>

        <div className="button-group">
          <Button text="Save" onClick={handleSave} isLoading={false} />
          {isEditMode && onDelete && (
            <div
              className="delete-btn add-btn-white"
              onClick={onDelete}
            >
              <TrashIcon /> Delete
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FollowOnModal;
